import type { RowSelectionState } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { useSelectedFilterUsers, useTenantList } from "~/features/admin/tenants/hooks";
import type { FilterUserOption } from "~/features/admin/tenants/types";
import { toTenantRowList } from "~/features/admin/tenants/utils/transformers";
import { v2Route } from "~/v2/routes";
import { TenantsPresenter } from "./tenants.presenter";

const DEFAULT_PAGE_SIZE = 25;

export function TenantsContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.admin.tenants);

  const selectedUserIds = useMemo(() => params.userIds ?? [], [params.userIds]);
  const where = useMemo(
    () =>
      selectedUserIds.length > 0
        ? { _and: selectedUserIds.map((id) => ({ userTenants: { userId: { _eq: id } } })) }
        : {},
    [selectedUserIds],
  );

  const { data, refetch } = useTenantList(where);
  const rows = useMemo(() => toTenantRowList(data.tenants), [data.tenants]);
  const selectedUsers = useSelectedFilterUsers(selectedUserIds);

  // The text input is driven by local state for instant feedback. The URL is only a
  // background mirror (sharable / restored on reload + back-forward): React Router v7 applies
  // navigation updates in a transition, so binding the input's value straight to `params.search`
  // makes fast typing lag. We adopt the URL value into local state only on a *genuine external*
  // change (back/forward, shared link): the URL changed since we last saw it AND it differs from
  // what we have locally. Our own writes — which the URL only lags toward — match local, so they
  // never clobber the in-progress value.
  const urlSearch = params.search ?? "";
  const [search, setSearch] = useState(urlSearch);
  const lastUrlSearchRef = useRef(urlSearch);
  if (urlSearch !== lastUrlSearchRef.current) {
    lastUrlSearchRef.current = urlSearch;
    if (urlSearch !== search) setSearch(urlSearch);
  }

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedTenant = params.selectedTenant ?? null;

  const onSearchChange = useCallback(
    (next: string) => {
      setSearch(next);
      setParams((prev) => ({ ...prev, search: next ? next : undefined, page: undefined }));
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next })),
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) =>
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      })),
    [setParams],
  );

  const onRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const onUserFilterChange = useCallback(
    (users: FilterUserOption[]) =>
      setParams((prev) => ({
        ...prev,
        userIds: users.length > 0 ? users.map((u) => u.id) : undefined,
        page: undefined,
      })),
    [setParams],
  );

  const onSelect = useCallback((id: string) => setParams((prev) => ({ ...prev, selectedTenant: id })), [setParams]);
  const onCloseSelected = useCallback(() => setParams((prev) => ({ ...prev, selectedTenant: undefined })), [setParams]);

  // Bulk-install selection is transient UI state (not URL-mirrored).
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const onBulkInstallCompleted = useCallback(() => {
    setRowSelection({});
    void refetch();
  }, [refetch]);

  return (
    <TenantsPresenter
      rows={rows}
      search={search}
      onSearchChange={onSearchChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onRefresh={onRefresh}
      selectedUsers={selectedUsers}
      onUserFilterChange={onUserFilterChange}
      selectedTenant={selectedTenant}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      onBulkInstallCompleted={onBulkInstallCompleted}
    />
  );
}
